import React from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { User, Mail, AlertTriangle, Briefcase, MapPin } from 'lucide-react'
import { Badge } from 'components/ui/Badge'
import { Candidate } from 'lib/api'

interface DeleteCandidateModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  candidate: Candidate | null
  loading: boolean
}

export function DeleteCandidateModal({ isOpen, onClose, onConfirm, candidate, loading }: DeleteCandidateModalProps) {
  const { t } = useTranslation()

  if (!candidate) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('candidates.deleteCandidate')}
      className="max-w-md"
    >
      <div className="space-y-6 p-4">
        <div className="flex items-center space-x-3 text-red-600 dark:text-red-400">
          <AlertTriangle className="h-6 w-6" />
          <h3 className="text-lg font-semibold text-foreground">{t('candidates.messages.confirmDelete')}</h3>
        </div>

        <div className="rounded-lg border p-4 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200">
          <p className="text-sm font-medium">{t('candidates.messages.deleteWarning')}</p>
          <p className="text-xs mt-1">{t('candidates.messages.deleteWarningDetails')}</p>
        </div>

        <div className="p-4 bg-muted rounded-lg border border-border">
          <div className="flex items-start space-x-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2">
                <h4 className="font-semibold text-foreground truncate">{candidate.name}</h4>
                <Badge 
                  variant={candidate.status === 'hired' ? 'default' : 'secondary'}
                  className="text-xs"
                >
                  {t(`candidates.status.${candidate.status}`)}
                </Badge>
              </div>
              
              <div className="space-y-1 text-sm">
                <div className="flex items-center text-muted-foreground">
                  <Mail className="h-3 w-3 mr-2" />
                  <span className="truncate">{candidate.email}</span>
                </div>
                
                <div className="flex items-center text-muted-foreground">
                  <Briefcase className="h-3 w-3 mr-2" />
                  <span className="truncate">{candidate.role}</span>
                </div>
                
                {candidate.location && (
                  <div className="flex items-center text-muted-foreground">
                    <MapPin className="h-3 w-3 mr-2" />
                    <span className="truncate">{candidate.location}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-6 p-4 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={loading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={onConfirm}
          loading={loading}
        >
          {loading ? t('common.deleting') : t('common.delete')}
        </Button>
      </div>
    </Modal>
  )
}
